<template>
    <div class="student-portal-inquiries" :class="{ dark }">
        <div class="student-portal-inquiries__inner">
            <header class="student-portal-inquiries__header">
                <div class="student-portal-inquiries__header-main">
                    <h1 class="card-title">Minhas Dúvidas</h1>
                    <p class="card-subtitle">
                        Visualize e gerencie suas dúvidas enviadas
                    </p>
                </div>
                <v-btn
                    color="#1ac4cd"
                    variant="elevated"
                    prepend-icon="mdi-plus"
                    class="student-portal-inquiries__new-btn"
                    @click="openDialogNewInquiry"
                >
                    Nova Dúvida
                </v-btn>
            </header>

            <!-- Loading -->
            <v-card
                v-if="!hasReady"
                class="portal-inquiries__card portal-inquiries__card--surface pa-8"
                elevation="0"
            >
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="64"
                        ></v-progress-circular>
                        <p class="text-center mt-4 student-portal-inquiries__loading-text">
                            Carregando dúvidas...
                        </p>
                    </v-col>
                </v-row>
            </v-card>

            <!-- Inquiries Table -->
            <v-card
                v-else
                elevation="0"
                class="portal-inquiries__card portal-inquiries__card--surface p-2 p-md-4"
            >
                        <div class="portal-inquiries__filters mb-4">
                            <v-text-field
                                v-model.trim="filters.query"
                                class="portal-inquiries__filter-search"
                                label="Buscar por título"
                                variant="outlined"
                                density="comfortable"
                                clearable
                                prepend-inner-icon="mdi-magnify"
                                hide-details
                            />
                            <v-select
                                v-model="filters.status"
                                class="portal-inquiries__filter-status"
                                :items="statusFilterOptions"
                                item-title="label"
                                item-value="value"
                                label="Status"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <v-select
                                v-model="filters.period"
                                class="portal-inquiries__filter-period"
                                :items="periodFilterOptions"
                                item-title="label"
                                item-value="value"
                                label="Período"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <v-btn
                                variant="tonal"
                                color="grey-darken-1"
                                prepend-icon="mdi-filter-off-outline"
                                :disabled="!hasActiveFilters"
                                @click="clearFilters"
                            >
                                Limpar
                            </v-btn>
                        </div>

                        <div class="portal-inquiries__filters-summary mb-3">
                            <span>
                                {{ filteredData.length }}
                                {{ filteredData.length === 1 ? 'dúvida encontrada' : 'dúvidas encontradas' }}
                            </span>
                        </div>

                        <div class="portal-inquiries__table-wrap">
                            <v-data-table
                                :headers="headers"
                                :items="filteredData"
                                :items-per-page="itemsPerPage"
                                :hide-default-footer="shouldHidePagination"
                                class="inquiries-table elevation-0"
                                no-data-text="Nenhuma dúvida encontrada"
                            >
                            <!-- Title Column -->
                            <template v-slot:[`item.title`]="{ item }">
                                <div class="font-weight-medium text-body-1 text-left py-2 inquiry-title-cell">
                                    {{ item.title }}
                                    <div class="d-md-none inquiry-title-cell__date">
                                        {{ formatarData(item.dateInsert) }}
                                    </div>
                                </div>
                            </template>

                            <!-- Date Column -->
                            <template v-slot:[`item.dateInsert`]="{ item }">
                                <div class="font-weight-medium d-none d-md-block">
                                    {{ formatarData(item.dateInsert) }}
                                </div>
                            </template>

                            <!-- Status Column -->
                            <template v-slot:[`item.status`]="{ item }">
                                <v-chip
                                    :color="item.status == 0 ? 'warning' : 'success'"
                                    size="small"
                                    variant="flat"
                                >
                                    {{ item.status == 0 ? 'Aguardando' : 'Respondido' }}
                                </v-chip>
                            </template>

                            <!-- Actions Column -->
                            <template v-slot:[`item.actions`]="{ item, index }">
                                <div class="d-flex gap-2 justify-center inquiry-actions-cell">
                                    <v-btn
                                        color="#1ac4cd"
                                        variant="text"
                                        icon="mdi-eye"
                                        size="small"
                                        @click="openDetailsDialog(item, index)"
                                    ></v-btn>
                                    <v-btn
                                        color="error"
                                        variant="text"
                                        icon="mdi-delete"
                                        size="small"
                                        @click="openDeleteModal(item, index)"
                                    ></v-btn>
                                </div>
                            </template>

                            <!-- Empty State -->
                            <template v-slot:no-data>
                                <div class="text-center pa-8">
                                    <v-icon
                                        size="80"
                                        color="grey-lighten-1"
                                        class="mb-4"
                                    >
                                        mdi-help-circle
                                    </v-icon>
                                    <h3 class="text-h5 mb-2">
                                        Nenhuma dúvida encontrada
                                    </h3>
                                    <p class="text-body-1 text-medium-emphasis">
                                        Crie uma nova dúvida para começar
                                    </p>
                                </div>
                            </template>
                            </v-data-table>
                        </div>
                    </v-card>
        </div>
        <v-dialog
            class="new-inquiry-dialog"
            v-model="newInquiryDialog"
            max-width="640"
        >
            <v-card class="dialog-card new-inquiry-dialog__card pa-4 pa-md-7">
                <div class="modal-header mb-4 mb-md-7">
                    <h5 class="modal-title formInquiryStudent">
                        Criar Nova Dúvida
                    </h5>
                </div>
                <v-card-text class="dialog-content p-0">
                    <v-text-field
                        v-model="newInquiry.title"
                        label="Título da Dúvida"
                        required
                        class="dialog-input"
                    ></v-text-field>
                    <v-textarea
                        v-model="newInquiry.description"
                        label="Descrição da Dúvida"
                        required
                        auto-grow
                        rows="8"
                        outlined
                        class="new-inquiry-dialog-textarea"
                    ></v-textarea>
                </v-card-text>
                <v-card-actions class="dialog-actions">
                    <v-btn
                        class="dialog-btn cancel"
                        text
                        @click="newInquiryDialog = false"
                        >Cancelar</v-btn
                    >
                    <v-btn
                        class="dialog-btn confirm"
                        text
                        @click="createNewInquiryStudent"
                        :disabled="!newInquiry.title"
                        >Criar</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="deletInquiryDialog"
            max-width="500"
            class="delete-inquiry-dialog"
        >
            <v-card class="dialog-card delete-inquiry-dialog__card">
                <div class="modal-header mb-3">
                    <h5 class="modal-title">
                        Deletar Dúvida
                    </h5>
                </div>
                <v-card-text class="dialog-content p-0">
                    <p>Você tem certeza que deseja deletar essa dúvida?</p>
                </v-card-text>
                <v-card-actions class="dialog-actions px-3">
                    <v-btn
                        class="dialog-btn cancel"
                        text
                        @click="deletInquiryDialog = false"
                        >Cancelar</v-btn
                    >
                    <v-btn
                        class="dialog-btn confirm"
                        text
                        @click="deletInquiry()"
                        >Deletar</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Details Dialog (layout portal) -->
        <v-dialog
            v-model="detailsDialog"
            max-width="800"
            scrollable
            class="portal-inquiry-detail-dialog"
        >
            <v-card
                v-if="selectedInquiry"
                elevation="0"
                class="portal-inquiry-detail-card"
                :class="{ 'portal-inquiry-detail-card--dark': dark }"
            >
                <v-card-title class="portal-inquiry-detail-card__head">
                    <div class="portal-inquiry-detail-card__head-main">
                        <p class="portal-inquiry-detail-card__eyebrow">
                            Detalhe da dúvida
                        </p>
                        <h2 class="portal-inquiry-detail-card__title">
                            {{ selectedInquiry.title }}
                        </h2>
                        <div class="portal-inquiry-detail-card__meta">
                            <span class="portal-inquiry-detail-card__date">
                                Enviado em
                                {{ formatarData(selectedInquiry.dateInsert) }}
                            </span>
                            <v-chip
                                size="small"
                                variant="tonal"
                                :color="
                                    selectedInquiry.status == 0
                                        ? 'warning'
                                        : 'success'
                                "
                            >
                                {{
                                    selectedInquiry.status == 0
                                        ? 'Aguardando'
                                        : 'Respondido'
                                }}
                            </v-chip>
                        </div>
                    </div>
                    <v-btn
                        icon="mdi-close"
                        variant="text"
                        density="comfortable"
                        class="portal-inquiry-detail-card__close"
                        aria-label="Fechar"
                        @click="detailsDialog = false"
                    ></v-btn>
                </v-card-title>

                <v-divider class="portal-inquiry-detail-card__divider" />

                <v-card-text class="portal-inquiry-detail-card__body">
                    <section class="portal-inquiry-detail-card__section">
                        <h3 class="portal-inquiry-detail-card__section-title">
                            Mensagem
                        </h3>
                        <div
                            class="portal-inquiry-detail-card__message description-inquiry"
                            v-html="selectedInquiry.description"
                        ></div>
                    </section>

                    <section class="portal-inquiry-detail-card__section">
                        <h3 class="portal-inquiry-detail-card__section-title">
                            Respostas
                        </h3>
                        <div class="portal-inquiry-detail-card__replies">
                            <ReplyInquiry
                                :id="selectedInquiry.id"
                                :messages="selectedInquiry.messages"
                            ></ReplyInquiry>
                        </div>
                    </section>

                    <div
                        v-show="selectedInquiry.status == 0"
                        class="portal-inquiry-detail-card__alert-wrap"
                    >
                        <v-alert
                            type="warning"
                            variant="tonal"
                            density="comfortable"
                            class="portal-inquiry-detail-card__alert"
                        >
                            Aguardando resposta da equipe.
                        </v-alert>
                    </div>

                    <section
                        class="portal-inquiry-detail-card__section portal-inquiry-detail-card__section--composer"
                    >
                        <h3 class="portal-inquiry-detail-card__section-title">
                            Novo comentário
                        </h3>
                        <v-textarea
                            v-model="comment[selectedInquiryIndex]"
                            label="Escreva seu comentário"
                            auto-grow
                            rows="3"
                            variant="outlined"
                            density="comfortable"
                            :hide-details="'auto'"
                            class="portal-inquiry-detail-card__textarea"
                        ></v-textarea>
                        <v-btn
                            color="#1ac4cd"
                            variant="elevated"
                            block
                            class="portal-inquiry-detail-card__submit mt-3"
                            @click="
                                newComment(
                                    selectedInquiry.id,
                                    selectedInquiryIndex
                                )
                            "
                        >
                            Enviar comentário
                        </v-btn>
                    </section>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import NewInquiry from '@/components/Student/Dashboard_Unique/Inquiry/New_Inquiry.vue';
    import ReplyInquiry from '@/components/Student/Dashboard_Unique/Inquiry/Reply_Inquiry.vue';

    import api from '@/utils/portal/inquiry.js';
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';


    export default {
        data() {
            return {
                active: 0,
                data: [],
                hasReady: false,
                comment: [],
                activeInquiries: {},
                newInquiryDialog: false,
                newInquiry: {
                    title: '',
                    description: '',
                },
                deletInquiryDialog: false,
                deletedInquiry: {
                    id: '',
                    index: '',
                },
                detailsDialog: false,
                selectedInquiry: null,
                selectedInquiryIndex: null,
                itemsPerPage: 10,
                filters: {
                    query: '',
                    status: 'all',
                    period: 'all',
                },
                statusFilterOptions: [
                    { label: 'Todos', value: 'all' },
                    { label: 'Aguardando', value: 0 },
                    { label: 'Respondido', value: 1 },
                ],
                periodFilterOptions: [
                    { label: 'Todo período', value: 'all' },
                    { label: 'Últimos 7 dias', value: 7 },
                    { label: 'Últimos 30 dias', value: 30 },
                    { label: 'Últimos 90 dias', value: 90 },
                ],
                headers: [
                    {
                        title: 'Título',
                        key: 'title',
                        sortable: true,
                        width: '40%',
                        minWidth: 200,
                        align: 'start',
                    },
                    {
                        title: 'Data de Envio',
                        key: 'dateInsert',
                        sortable: true,
                        width: '20%',
                        align: 'center',
                        class: 'd-none d-md-table-cell',
                    },
                    {
                        title: 'Status',
                        key: 'status',
                        sortable: true,
                        width: '15%',
                        align: 'center',
                    },
                    {
                        title: 'Ações',
                        key: 'actions',
                        sortable: false,
                        width: '25%',
                        align: 'center',
                    },
                ],
            };
        },
        props: {},
        computed: {
            ...mapState(['token']),
            ...mapState(['dark']),
            shouldHidePagination() {
                return this.filteredData.length <= this.itemsPerPage;
            },
            hasActiveFilters() {
                return (
                    this.filters.query !== '' ||
                    this.filters.status !== 'all' ||
                    this.filters.period !== 'all'
                );
            },
            filteredData() {
                const q = this.filters.query.toLowerCase();
                const status = this.filters.status;
                const period = this.filters.period;
                const now = new Date();

                return this.data.filter((item) => {
                    const byQuery =
                        !q ||
                        String(item.title || '')
                            .toLowerCase()
                            .includes(q);

                    const byStatus = status === 'all' || item.status === status;

                    let byPeriod = true;
                    if (period !== 'all' && item.dateInsert) {
                        const dt = new Date(item.dateInsert);
                        if (Number.isNaN(dt.getTime())) {
                            byPeriod = false;
                        } else {
                            const diffMs = now.getTime() - dt.getTime();
                            const diffDays = diffMs / (1000 * 60 * 60 * 24);
                            byPeriod = diffDays <= Number(period);
                        }
                    }

                    return byQuery && byStatus && byPeriod;
                });
            },
        },
        components: {
            NewInquiry,
            ReplyInquiry,
        },
        methods: {
            handleOpenModalInquiry() {
                this.openDialogNewInquiry = false;
            },
            openDetailsDialog(item, index) {
                this.selectedInquiry = item;
                this.selectedInquiryIndex = index;
                this.detailsDialog = true;
            },
            toggleInquiry(index) {
                this.activeInquiries[index] = !this.activeInquiries[index];
            },
            openDialogNewInquiry() {
                this.newInquiryDialog = true;
            },
            async createNewInquiryStudent() {
                const payload = {
                    title: this.newInquiry.title,
                    description: this.newInquiry.description,
                    status: 1,
                };
                try {
                    const response = await api.newInquiry(this.token, payload);
                    this.getDataInquirys();
                    toast.success('Dúvida criada com sucesso!')
                    this.newInquiryDialog = false;
                    this.newInquiry = { title: '', description: '' };
                } catch (error) {
                    toast.error('Erro ao criar dúvida');
                    console.error('Erro ao criar dúvida:', error);
                }
            },
            async getDataInquirys() {
                this.hasReady = false;
                try {
                    const response = await api.getDataListInquiry(this.token);
                    this.data = response;
                    this.activeInquiries[0] = true;
                    this.transformFirstMessagesInDescription();
                    this.hasReady = true;
                } catch (error) {
                    toast.error('Erro ao carregar as dúvidas');
                    console.error('Erro na listagem da notificações');
                }
            },
            formatarData(data) {
                const dataObj = new Date(data);
                const dia = dataObj.getDate();
                const mes = dataObj.getMonth() + 1;
                const ano = dataObj.getFullYear() % 100;

                const diaFormatado = dia < 10 ? '0' + dia : dia;
                const mesFormatado = mes < 10 ? '0' + mes : mes;
                const anoFormatado = ano < 10 ? '0' + ano : ano;

                return `${diaFormatado}/${mesFormatado}/${anoFormatado}`;
            },
            clearFilters() {
                this.filters.query = '';
                this.filters.status = 'all';
                this.filters.period = 'all';
            },
            async newComment(id, index) {
                const obj = {
                    message: this.comment[index],
                };
                try {
                    const response = await api.newComment(this.token, obj, id);
                    location.reload();
                } catch (error) {
                    toast.error('Erro ao enviar comentário da dúvida');
                    console.error('Erro ao enviar comentário da dúvida');
                }
            },
            transformFirstMessagesInDescription() {
                if (this.data.length > 0) {
                    for (let i = 0; i < this.data.length; i++) {
                        if (
                            this.data[i].messages &&
                            this.data[i].messages.length > 0
                        ) {
                            const firstMessage = this.data[i].messages.pop(
                                this.data[i].messages.length - 1
                            );
                            this.data[i].description = firstMessage.message;
                        } else {
                            this.data[i].description = '';
                        }
                    }
                }
            },
            openDeleteModal(item, index) {
                this.deletedInquiry.id = item.id;
                this.deletedInquiry.index = index;
                this.deletInquiryDialog = true;
            },
            deletInquiry() {
                const deletedInquiry = this.data.find(
                    (inquiry) => inquiry.id === this.deletedInquiry.id
                );
                const payload = {
                    ...deletedInquiry,
                    status: 9,
                };
                api
                    .updateInquiry(this.token, payload, this.deletedInquiry.id)
                    .then(() => {
                        this.data.splice(this.deletedInquiry.index, 1);
                        this.deletInquiryDialog = false;
                        this.deletedInquiry = { id: '', index: '' };
                    })
                    .catch((error) => {
                        console.error('Erro ao deletar a dúvida:', error);
                    });
            }
        },
        created() {
            this.getDataInquirys();
        },
        watch: {
            openDialogNewInquiry(novoValor) {
                if (novoValor == false) {
                    setTimeout(() => {
                        this.getDataInquirys();
                    }, 1000);
                }
                document.body.style.overflow = novoValor ? 'hidden' : '';
            },
        },
    };
</script>

<style scoped>
    /* Alinhado ao dashboard de cursos (Courses.vue): largura máxima e tokens */
    .student-portal-inquiries {
        --dash-bg: #eef0f4;
        --dash-bg-elevated: #ffffff;
        --dash-text: #111827;
        --dash-text-muted: #6b7280;
        --dash-border: rgba(15, 23, 42, 0.08);
        min-height: 100%;
        background-color: var(--dash-bg);
        box-sizing: border-box;
    }

    .student-portal-inquiries.dark {
        --dash-bg: #0f0e12;
        --dash-bg-elevated: #1a191f;
        --dash-text: #f3f4f6;
        --dash-text-muted: #9ca3af;
        --dash-border: rgba(255, 255, 255, 0.08);
    }

    .student-portal-inquiries__inner {
        max-width: 1920px;
        margin: 0 auto;
        padding: 18px 14px 32px;
        box-sizing: border-box;
    }

    .student-portal-inquiries__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        text-align: left;
        margin-bottom: 28px;
    }

    .student-portal-inquiries__header-main {
        min-width: 0;
    }

    .student-portal-inquiries__new-btn {
        flex-shrink: 0;
        margin-top: 4px;
    }

    .student-portal-inquiries__loading-text {
        color: var(--dash-text-muted);
    }

    .portal-inquiries__card--surface {
        background-color: var(--dash-bg-elevated) !important;
        color: var(--dash-text);
        border: 1px solid #e8eaf0;
        border-radius: 12px;
        box-shadow:
            0 1px 3px rgba(37, 8, 145, 0.06),
            0 6px 22px rgba(15, 23, 42, 0.06);
    }

    .portal-inquiries__card {
        padding: 14px !important;
    }

    .portal-inquiries__table-wrap {
        width: 100%;
        overflow-x: auto;
    }

    .inquiry-title-cell__date {
        margin-top: 4px;
        font-size: 0.78rem;
        color: var(--dash-text-muted);
        font-weight: 500;
    }

    :deep(.inquiries-table) {
        border: 1px solid #e8eaf0;
        border-radius: 10px;
        overflow: hidden;
    }

    :deep(.inquiries-table .v-data-table__th) {
        background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
        color: #374151;
        font-weight: 600;
        border-bottom: 1px solid #eef0f4;
    }

    :deep(.inquiries-table .v-data-table__tr:hover) {
        background: rgba(37, 8, 145, 0.03);
    }

    :deep(.inquiries-table .v-data-table__td) {
        border-bottom: 1px solid #f1f3f7;
    }

    .portal-inquiries__filters {
        display: grid;
        grid-template-columns: minmax(260px, 1.6fr) minmax(160px, 0.8fr) minmax(
                180px,
                0.9fr
            ) auto;
        gap: 12px;
        align-items: center;
        border: 1px solid #e8eaf0;
        border-radius: 10px;
        padding: 10px;
        background: #fafbfc;
    }

    .portal-inquiries__filters-summary {
        font-size: 0.9rem;
        color: var(--dash-text-muted);
    }

    @media (max-width: 768px) {
        .student-portal-inquiries__inner {
            padding: 14px 10px 24px;
        }

        .student-portal-inquiries__header {
            margin-bottom: 14px;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
        }

        .student-portal-inquiries__new-btn {
            align-self: stretch;
            margin-top: 0;
            min-height: 42px;
        }

        .portal-inquiries__card {
            padding: 10px !important;
        }

        .portal-inquiries__filters {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 8px;
        }

        .portal-inquiries__filters-summary {
            font-size: 0.82rem;
            margin-bottom: 8px !important;
        }

        :deep(.inquiries-table table) {
            min-width: 620px;
        }

        :deep(.inquiries-table .v-data-table__th),
        :deep(.inquiries-table .v-data-table__td) {
            padding: 10px 8px !important;
        }

        .inquiry-actions-cell {
            min-width: 88px;
            justify-content: flex-end !important;
        }

        .card-title {
            font-size: 24px;
            margin-bottom: 4px;
        }

        .card-subtitle {
            font-size: 14px;
        }
    }

    @media (max-width: 959px) {
        :deep(.inquiries-table th:nth-child(2)),
        :deep(.inquiries-table td:nth-child(2)) {
            display: none !important;
        }
    }

    .dark.student-portal-inquiries :deep(.inquiries-table) {
        background-color: var(--dash-bg-elevated);
        color: var(--dash-text);
    }

    .dark.student-portal-inquiries :deep(.inquiries-table .v-data-table__th) {
        background-color: #252730;
        color: var(--dash-text-muted);
        border-bottom-color: #3d414d;
    }

    .dark.student-portal-inquiries :deep(.inquiries-table .v-data-table__td) {
        color: var(--dash-text);
        border-bottom-color: #2d313c;
    }

    .dark.student-portal-inquiries :deep(.inquiries-table .v-data-table__tr:hover) {
        background: rgba(109, 90, 205, 0.12);
    }

    .dark.student-portal-inquiries :deep(.inquiries-table) {
        border-color: #3d414d;
    }

    .dark.student-portal-inquiries :deep(.v-data-table-footer) {
        background-color: var(--dash-bg-elevated);
        color: var(--dash-text-muted);
        border-color: var(--dash-border);
    }

    .dark.student-portal-inquiries .portal-inquiries__card--surface {
        border-color: #353945;
        box-shadow:
            0 1px 4px rgba(0, 0, 0, 0.35),
            0 8px 22px rgba(0, 0, 0, 0.24);
    }

    .dark.student-portal-inquiries .portal-inquiries__filters {
        background: #1e2028;
        border-color: #3d414d;
    }

    .dark.student-portal-inquiries :deep(.v-divider) {
        border-color: var(--dash-border);
    }

    .inquiry-details {
        background-color: #f5f5f5;
        border-radius: 8px;
        padding: 16px;
    }

    .dark .inquiry-details {
        background-color: #1e1e1e;
    }

    .description-inquiry {
        color: #333;
    }

    .dark .description-inquiry {
        color: #e0e0e0;
    }

    /* Modal detalhe da dúvida (padrão portal; ok com teleport do v-dialog) */
    .portal-inquiry-detail-card {
        --pid-bg: #ffffff;
        --pid-text: #111827;
        --pid-muted: #6b7280;
        --pid-border: rgba(15, 23, 42, 0.1);
        --pid-elevated: #f3f4f6;
        --pid-chip-bg: rgba(26, 196, 205, 0.12);
        background: var(--pid-bg) !important;
        color: var(--pid-text);
        border: 1px solid var(--pid-border);
        border-radius: 14px;
        box-shadow:
            0 10px 40px rgba(15, 23, 42, 0.12),
            0 1px 3px rgba(15, 23, 42, 0.06);
        overflow: hidden;
    }

    .portal-inquiry-detail-card--dark {
        --pid-bg: #1a191f;
        --pid-text: #f3f4f6;
        --pid-muted: #9ca3af;
        --pid-border: rgba(255, 255, 255, 0.1);
        --pid-elevated: #252730;
        --pid-chip-bg: rgba(26, 196, 205, 0.2);
        box-shadow:
            0 16px 48px rgba(0, 0, 0, 0.45),
            0 1px 4px rgba(0, 0, 0, 0.35);
    }

    .portal-inquiry-detail-card__head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        padding: 20px 20px 16px !important;
        white-space: normal;
    }

    .portal-inquiry-detail-card__head-main {
        min-width: 0;
        flex: 1 1 auto;
        text-align: left;
    }

    .portal-inquiry-detail-card__eyebrow {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: 700;
        color: var(--pid-muted);
        margin: 0 0 6px;
    }

    .portal-inquiry-detail-card__title {
        font-size: 1.2rem;
        font-weight: 700;
        line-height: 1.35;
        margin: 0 0 10px;
        color: var(--pid-text);
    }

    .portal-inquiry-detail-card__meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
    }

    .portal-inquiry-detail-card__date {
        font-size: 0.82rem;
        color: var(--pid-muted);
    }

    .portal-inquiry-detail-card__close {
        flex-shrink: 0;
        margin-top: -2px;
        color: var(--pid-muted) !important;
    }

    .portal-inquiry-detail-card__divider {
        border-color: var(--pid-border) !important;
        opacity: 1;
    }

    .portal-inquiry-detail-card__body {
        padding: 20px !important;
        background: var(--pid-bg);
    }

    .portal-inquiry-detail-card__section {
        margin-bottom: 22px;
    }

    .portal-inquiry-detail-card__section--composer {
        margin-bottom: 0;
        margin-top: 4px;
        padding-top: 18px;
        border-top: 1px dashed var(--pid-border);
    }

    .portal-inquiry-detail-card__section-title {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        font-weight: 700;
        color: var(--pid-muted);
        margin: 0 0 10px;
        text-align: left;
    }

    .portal-inquiry-detail-card__message {
        padding: 14px 16px;
        border-radius: 10px;
        background: var(--pid-elevated);
        border: 1px solid var(--pid-border);
        font-size: 0.94rem;
        line-height: 1.55;
        color: var(--pid-text);
        text-align: left;
        word-break: break-word;
    }

    .portal-inquiry-detail-card__message.description-inquiry :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
    }

    .portal-inquiry-detail-card__replies :deep(.reply-item) {
        background: var(--pid-elevated);
        border: 1px solid var(--pid-border);
        border-radius: 10px;
        margin: 0 0 12px;
        padding: 14px 16px;
    }

    .portal-inquiry-detail-card__replies :deep(.reply-item:last-of-type) {
        margin-bottom: 0;
    }

    .portal-inquiry-detail-card__replies :deep(.content_reply) {
        font-size: 0.92rem;
        line-height: 1.5;
        color: var(--pid-text);
        margin-bottom: 12px;
        text-align: left;
    }

    .portal-inquiry-detail-card__replies :deep(.autor) {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 12px;
    }

    .portal-inquiry-detail-card__replies :deep(.autor .logo) {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: var(--pid-chip-bg);
        color: #0f766e;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.72rem;
        font-weight: 700;
    }

    .portal-inquiry-detail-card--dark
        .portal-inquiry-detail-card__replies
        :deep(.autor .logo) {
        color: #5eead4;
    }

    .portal-inquiry-detail-card__replies :deep(.autor .name) {
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--pid-text);
    }

    .portal-inquiry-detail-card__replies :deep(.autor .date) {
        font-size: 0.75rem;
        color: var(--pid-muted);
    }

    .portal-inquiry-detail-card__replies :deep(.empty-state) {
        border: 1px dashed var(--pid-border);
        border-radius: 10px;
        padding: 20px 16px;
        color: var(--pid-muted);
        font-style: normal;
        font-size: 0.9rem;
        background: var(--pid-elevated);
        text-align: center;
    }

    .portal-inquiry-detail-card__alert-wrap {
        margin-bottom: 18px;
    }

    .portal-inquiry-detail-card__alert {
        border-radius: 10px;
    }

    .portal-inquiry-detail-card__submit {
        font-weight: 600;
    }

    @media (max-width: 600px) {
        .portal-inquiry-detail-card__head {
            padding: 16px 14px 12px !important;
        }

        .portal-inquiry-detail-card__title {
            font-size: 1.05rem;
        }

        .portal-inquiry-detail-card__body {
            padding: 16px 14px 18px !important;
        }
    }

    .gap-2 {
        gap: 8px;
    }

    .card-title {
        font-size: 28px;
        font-weight: 700;
        color: var(--dash-text);
        margin-bottom: 8px;
    }

    .card-subtitle {
        font-size: 16px;
        color: var(--dash-text-muted);
        margin-bottom: 0;
    }
</style>
